import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { Home, BookOpen, ListChecks, Menu, X } from 'lucide-react';
import Logo from '../Logo';

const navLinks = [
  { href: '/', label: 'Home', icon: Home },
  { href: '/directory', label: 'Directory', icon: BookOpen },
];

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [location] = useLocation();

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link href="/">
              <a className="flex items-center hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg">
                <Logo size="sm" />
              </a>
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center space-x-6">
              {navLinks.map(({ href, label, icon: Icon }) => (
                <Link key={href} href={href}>
                  <a className="flex items-center text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md px-1 py-0.5">
                    <Icon className="w-4 h-4 mr-1.5" />
                    {label}
                  </a>
                </Link>
              ))}

              <Link href="/questionnaire">
                <a className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                  <ListChecks className="w-4 h-4 mr-1.5" />
                  Get Started
                </a>
              </Link>
            </div>

            {/* Mobile: Get Started + Hamburger */}
            <div className="flex md:hidden items-center gap-3">
              <Link href="/questionnaire">
                <a className="flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                  Get Started
                </a>
              </Link>
              <button
                onClick={() => setIsOpen(!isOpen)}
                aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={isOpen}
                aria-controls="mobile-menu"
                className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          aria-hidden="true"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile menu drawer */}
      <div
        id="mobile-menu"
        role="dialog"
        aria-label="Navigation menu"
        className={`fixed top-16 left-0 right-0 z-40 bg-white border-b border-gray-200 shadow-lg md:hidden transform transition-all duration-200 ease-in-out ${
          isOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0 pointer-events-none'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
          {navLinks.map(({ href, label, icon: Icon }) => (
            <Link key={href} href={href}>
              <a className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors font-medium text-base focus:outline-none focus:ring-2 focus:ring-blue-500">
                <Icon className="w-5 h-5 flex-shrink-0" />
                {label}
              </a>
            </Link>
          ))}
          <Link href="/questionnaire">
            <a className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
              <ListChecks className="w-5 h-5 flex-shrink-0" />
              Get My Software Blueprint
            </a>
          </Link>
        </div>
      </div>
    </>
  );
}
